import {
  Button,
  Create,
  CreateButton,
  Datagrid,
  Edit,
  List,
  TextField,
  useNotify,
  useRedirect,
  useResourceContext,
  BooleanField,
  NumberField,
  FunctionField,
  BulkDeleteButton,
  BulkExportButton,
  TopToolbar,
} from 'react-admin';
import { ESTDateField } from './custom-ra-fields/est-date-field';
import { EndemicDriverForm } from './forms/endemic-drivers';

// Utility function to convert topic tag IDs string to array
function parseTopicTagIds(topicTagIds: string | string[] | undefined | null): string[] {
  if (!topicTagIds) return [];
  if (Array.isArray(topicTagIds)) return topicTagIds;
  if (typeof topicTagIds !== 'string') return [];
  return topicTagIds.replace(/\s/g, '').split(/[,\n]/).filter((id: string) => id.length > 0);
}

export const EndemicDriversList = () => (
  <List
    actions={<EndemicDriversListActions />}
    bulkActionButtons={
      <>
        <BulkExportButton />
        <BulkDeleteButton />
      </>
    }
  >
    <Datagrid rowClick="edit">
      <TextField source="id" />
      <TextField source="name" />
      <FunctionField
        label="Driver Type"
        render={(record: any) => {
          const typeMap: { [key: string]: string } = {
            chatbot: 'Chatbot',
            bragBar: 'Brag Bar',
          };
          return typeMap[record.driverContentType] || record.driverContentType;
        }}
      />
      <NumberField source="priority" />
      <BooleanField source="isApproved" />
      <ESTDateField source="startsAt" />
      <ESTDateField source="endsAt" />
      <FunctionField
        label="Targeting"
        render={(record: any) => {
          const targetingTypes = [];
          if (record.topicTagIds && record.topicTagIds.length > 0) {
            targetingTypes.push('Topic Tags');
          }
          if (record.targetListsList && record.targetListsList.length > 0) {
            targetingTypes.push('Target Lists');
          }
          if (
            (record.specialtyIdsList && record.specialtyIdsList.length > 0) ||
            (record.professionIdsList && record.professionIdsList.length > 0)
          ) {
            targetingTypes.push('Run of Specialty');
          }
          return targetingTypes.join(', ') || 'None';
        }}
      />
    </Datagrid>
  </List>
);

const EndemicDriversListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

export const EndemicDriverCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const resource = useResourceContext();

  const onSuccess = () => {
    notify('Saved new Endemic Driver');
    redirect('list', resource);
  };

  return (
    <Create
      transform={(data: any) => {
        const { targetListIdsList, specialtyIdsList, professionIdsList, ...rest } = data;
        return {
          ...rest,
          topicTagIds: parseTopicTagIds(data.topicTagIds),
          targetListIds: targetListIdsList || [],
          specialtyIds: specialtyIdsList || [],
          professionIds: professionIdsList || [],
        };
      }}
      mutationOptions={{ onSuccess }}
    >
      <EndemicDriverForm type="Create" />
    </Create>
  );
};

export const EndemicDriverEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const resource = useResourceContext();

  const onSuccess = () => {
    notify('Updated Endemic Driver');
    redirect('list', resource);
  };
  const onError = (error: any) => {
    console.error('❌ EndemicDriverEdit mutation error:', error);
    notify(error?.message || 'Failed to update Endemic Driver', { type: 'warning' });
  };

  return (
    <Edit
      mutationMode="pessimistic"
      transform={(data: any) => {
        const { targetListIdsList, targetListsList, specialtyIdsList, professionIdsList, ...rest } = data;
        return {
          ...rest,
          topicTagIds: parseTopicTagIds(data.topicTagIds),
          targetListIds: targetListIdsList || [],
          specialtyIds: specialtyIdsList || [],
          professionIds: professionIdsList || [],
        };
      }}
      mutationOptions={{ onSuccess, onError }}
    >
      <EndemicDriverForm type="Edit" />
    </Edit>
  );
};
